import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { ModelSelector } from '@/components/settings/ModelSelector'
import {
  getModelSettings,
  getAvailableModels,
  updateModelSettings,
  type ModelSettings,
  type GeminiModel,
} from '@/api/settings'
import { Loader2, Save, Check, AlertCircle } from 'lucide-react'

export default function Settings() {
  const user = useAuthStore((state) => state.user)

  // AI Models state
  const [models, setModels] = useState<GeminiModel[]>([])
  const [modelSettings, setModelSettings] = useState<ModelSettings | null>(null)
  const [isLoadingModels, setIsLoadingModels] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [originalSettings, setOriginalSettings] = useState<ModelSettings | null>(null)

  // Load models and settings on mount
  useEffect(() => {
    async function loadModelData() {
      try {
        setIsLoadingModels(true)
        const [settingsData, modelsData] = await Promise.all([
          getModelSettings(),
          getAvailableModels(),
        ])
        setModelSettings(settingsData)
        setOriginalSettings(settingsData)
        setModels(modelsData)
      } catch (error) {
        console.error('Failed to load model settings:', error)
      } finally {
        setIsLoadingModels(false)
      }
    }
    loadModelData()
  }, [])

  // Track changes
  useEffect(() => {
    if (modelSettings && originalSettings) {
      const changed =
        modelSettings.coordinator_model !== originalSettings.coordinator_model ||
        modelSettings.specialist_model !== originalSettings.specialist_model ||
        modelSettings.reasoning_model !== originalSettings.reasoning_model
      setHasChanges(changed)
    }
  }, [modelSettings, originalSettings])

  // Handle model change
  const handleModelChange = (
    field: keyof ModelSettings,
    value: string
  ) => {
    if (modelSettings) {
      setModelSettings({ ...modelSettings, [field]: value })
      setSaveStatus('idle')
    }
  }

  // Save settings
  const handleSave = async () => {
    if (!modelSettings || !hasChanges) return

    try {
      setIsSaving(true)
      setSaveError(null)
      const updated = await updateModelSettings(modelSettings)
      setModelSettings(updated)
      setOriginalSettings(updated)
      setSaveStatus('success')
      setHasChanges(false)

      // Reset success status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('Failed to save model settings:', error)
      setSaveStatus('error')
      setSaveError(error instanceof Error ? error.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-text mb-6">Settings</h1>

      {/* Profile Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-text mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-muted mb-1">
              Username
            </label>
            <div className="text-text">{user?.username || 'Unknown'}</div>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">Role</label>
            <div className="text-text capitalize">{user?.role || 'user'}</div>
          </div>
        </div>
      </div>

      {/* AI Models Section */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">AI Models</h2>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-3 py-1.5 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : saveStatus === 'success' ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSaving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : 'Save Changes'}
            </button>
          )}
        </div>

        {isLoadingModels ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-accent animate-spin" />
          </div>
        ) : modelSettings ? (
          <div className="space-y-6">
            <ModelSelector
              label="Coordinator Model"
              description="Main agent that routes requests to specialists"
              value={modelSettings.coordinator_model}
              models={models}
              onChange={(v) => handleModelChange('coordinator_model', v)}
            />

            <ModelSelector
              label="Specialist Model"
              description="Domain-specific agents (Home Assistant, UniFi, etc.) - use a cheaper model here"
              value={modelSettings.specialist_model}
              models={models}
              onChange={(v) => handleModelChange('specialist_model', v)}
            />

            <ModelSelector
              label="Reasoning Model"
              description="Complex tasks like code analysis (Claude specialist)"
              value={modelSettings.reasoning_model}
              models={models}
              onChange={(v) => handleModelChange('reasoning_model', v)}
            />

            {saveStatus === 'error' && saveError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {saveError}
              </div>
            )}

            <div className="text-xs text-text-muted pt-2 border-t border-surface-border">
              Note: Changes take effect on the next request. The service does not need to be restarted.
            </div>
          </div>
        ) : (
          <div className="text-text-muted text-sm">
            Failed to load model settings
          </div>
        )}
      </div>

      {/* Appearance Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-text mb-4">Appearance</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-text">Dark Theme</div>
              <div className="text-sm text-text-muted">
                Currently locked to dark theme
              </div>
            </div>
            <div className="text-accent">Always On</div>
          </div>
        </div>
      </div>

      {/* Connections Section */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-text mb-4">Connections</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-text">Home Assistant</div>
              <div className="text-sm text-text-muted">
                http://192.168.1.31:8123
              </div>
            </div>
            <div className="text-green-400 text-sm">Connected</div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-text">PostgreSQL</div>
              <div className="text-sm text-text-muted">192.168.1.37:5432</div>
            </div>
            <div className="text-green-400 text-sm">Connected</div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-text">Gemini AI</div>
              <div className="text-sm text-text-muted font-mono">
                {modelSettings?.coordinator_model || 'Loading...'}
              </div>
            </div>
            <div className="text-green-400 text-sm">Active</div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text mb-4">About</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-text-muted">Neuro-HA UI</span>
            <span className="text-text">v0.2.0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-muted">Neuro-HA Core</span>
            <span className="text-text">v0.2.0</span>
          </div>
          <div className="mt-4 pt-4 border-t border-surface-border">
            <a
              href="https://github.com/neuro-ha"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-hover"
            >
              View on GitHub
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
